import { useEffect, useState, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router';
import { Download, FileText } from 'lucide-react';
import { Badge, Button, PageShell, RadioGroup, SectionCard, Skeleton, toast } from '@/components/ui';
import { ApiClientError } from '@/lib/http-core';
import { friendlyMessage } from '@/lib/errors';
import { useHasRole } from '@/providers/AuthProvider';
import {
  fetchAttachmentUrl,
  useBusinessInquiryQuery,
  useResendMailMutation,
  useUpdateBusinessInquiryMutation,
} from '../hooks/useBusinessInquiries';
import { BUSINESS_INQUIRY_WRITE_ROLES } from '../lib/businessInquiryRbac';
import {
  INQUIRY_STATUSES,
  INQUIRY_STATUS_LABELS,
  INQUIRY_STATUS_VARIANT,
  formatFileSize,
  formatKst,
} from '../lib/businessInquiryLabels';
import type { BusinessInquiryDetail, InquiryStatus } from '../types/businessInquiry';

const MEMO_MAX = 2000;

const InfoRows = ({ rows }: { rows: ReadonlyArray<readonly [string, ReactNode]> }) => (
  <dl className="grid grid-cols-[120px_minmax(0,1fr)] text-[13px]">
    {rows.map(([label, value]) => (
      <div key={label} className="contents">
        <dt className="border-b px-4 py-2.5 font-semibold" style={{ background: 'var(--surface-1)', borderColor: 'var(--divider-default)' }}>
          {label}
        </dt>
        <dd className="min-w-0 border-b px-4 py-2.5" style={{ borderColor: 'var(--divider-default)' }}>
          {value}
        </dd>
      </div>
    ))}
  </dl>
);

/**
 * QA-628 BO-02 비즈니스 문의 상세 — 담당자 메일 [자세히 보기] 링크(/business-inquiries/{No})도 이 화면으로 온다.
 * 문의 내용은 텍스트로만 그린다(HTML 실행 방지, 줄바꿈 유지). 처리 상태·메모 저장·재발송은 AUDITOR 제외.
 */
export const BusinessInquiryDetailPage = () => {
  const { inquiryNo: param } = useParams();
  const inquiryNo = param && /^\d+$/.test(param) ? Number(param) : null;
  const navigate = useNavigate();
  const query = useBusinessInquiryQuery(inquiryNo);

  const backToList = () => navigate('/business-inquiries');
  const title = inquiryNo ? `비즈니스 문의 상세` : '비즈니스 문의';
  const breadcrumb = [{ label: '고객센터' }, { label: '비즈니스 문의', href: '/business-inquiries' }, { label: `No.${param ?? ''}` }];

  if (inquiryNo === null || query.isError) {
    const notFound = inquiryNo === null || (query.error instanceof ApiClientError && query.error.status === 404);
    return (
      <PageShell title={title} breadcrumb={breadcrumb}>
        <SectionCard>
          <p role="alert" className="text-[13px]" style={{ color: 'var(--status-danger-fg)' }}>
            {notFound ? '문의를 찾을 수 없습니다. 보관 기간이 지나 파기되었거나 없는 번호입니다.' : friendlyMessage(query.error, '문의를 불러오지 못했습니다.')}
          </p>
          <Button type="button" variant="outline" size="sm" className="mt-3" onClick={backToList}>
            목록
          </Button>
        </SectionCard>
      </PageShell>
    );
  }

  return (
    <PageShell title={title} breadcrumb={breadcrumb}>
      {query.isPending ? <Skeleton className="h-64" /> : <DetailBody detail={query.data} onBack={backToList} />}
    </PageShell>
  );
};

const DetailBody = ({ detail, onBack }: { detail: BusinessInquiryDetail; onBack: () => void }) => {
  const canWrite = useHasRole(BUSINESS_INQUIRY_WRITE_ROLES);
  const update = useUpdateBusinessInquiryMutation(detail.inquiryNo);
  const resend = useResendMailMutation(detail.inquiryNo);
  const [status, setStatus] = useState<InquiryStatus>(detail.status.code);
  const [memo, setMemo] = useState(detail.memo ?? '');
  const [opening, setOpening] = useState(false);

  // 저장·재발송으로 상세가 바뀌면 입력란도 서버 값으로 맞춘다.
  useEffect(() => {
    setStatus(detail.status.code);
    setMemo(detail.memo ?? '');
  }, [detail.status.code, detail.memo, detail.updatedAt]);

  const isWholesale = detail.types.some((t) => t.code === 'WHOLESALE');

  // 비공개 파일 — 누를 때마다 5분짜리 주소를 받는다. 팝업 차단을 피하려고 창을 먼저 연 뒤 주소를 넣는다.
  const openAttachment = async (disposition: 'inline' | 'attachment') => {
    const win = disposition === 'inline' ? window.open('', '_blank') : null;
    setOpening(true);
    try {
      const url = await fetchAttachmentUrl(detail.inquiryNo, disposition);
      if (win) win.location.href = url;
      else window.location.assign(url);
    } catch (e) {
      win?.close();
      toast.error(friendlyMessage(e, '사업자등록증을 열지 못했습니다.'));
    } finally {
      setOpening(false);
    }
  };

  const onSave = () =>
    update.mutate(
      { status, memo: memo.trim() },
      {
        onSuccess: () => toast.success('저장했습니다.'),
        onError: (e) => toast.error(friendlyMessage(e, '저장하지 못했습니다.')),
      },
    );

  const onResend = (logId: number) =>
    resend.mutate(logId, {
      onSuccess: (d) => {
        const row = d.mailLogs.find((l) => l.logId === logId);
        if (row?.status === 'SENT') toast.success('재발송했습니다.');
        else toast.error('재발송이 다시 실패했습니다. 발송 이력의 결과를 확인해 주세요.');
      },
      onError: (e) => toast.error(friendlyMessage(e, '재발송하지 못했습니다.')),
    });

  const updatedBy = detail.updatedBy
    ? detail.updatedBy.name
      ? `${detail.updatedBy.name}${detail.updatedBy.loginId ? ` (${detail.updatedBy.loginId})` : ''}`
      : detail.updatedBy.adminUserId
    : null;

  return (
    <div className="flex flex-col gap-4">
      <SectionCard
        title="문의 정보"
        aside={
          <Badge variant={INQUIRY_STATUS_VARIANT[detail.status.code] ?? 'neutral'} size="sm">
            {detail.status.label}
          </Badge>
        }
        padded={false}
      >
        <InfoRows
          rows={[
            ['No.', <span className="tabular-nums">{detail.inquiryNo}</span>],
            ['접수일시', <span className="tabular-nums">{formatKst(detail.createdAt, true) ?? '—'}</span>],
            ['접수 몰', detail.mall.label],
            [
              '문의 유형',
              <span className="flex flex-wrap gap-1">
                {detail.types.map((t) => (
                  <Badge key={t.code} variant="info" size="sm">
                    {t.label}
                  </Badge>
                ))}
              </span>,
            ],
            ['문의 제목', detail.title],
            [
              '문의 내용',
              <p className="whitespace-pre-wrap break-words" data-testid="business-inquiry-content">
                {detail.content}
              </p>,
            ],
          ]}
        />
      </SectionCard>

      {isWholesale ? (
        <SectionCard title="도매(사업자) 정보" padded={false}>
          <InfoRows
            rows={[
              ['판매국가', detail.salesCountries || '—'],
              [
                '사업자등록증',
                detail.attachment ? (
                  <span className="flex flex-wrap items-center gap-2">
                    <FileText size={14} aria-hidden="true" />
                    <button
                      type="button"
                      className="underline underline-offset-2"
                      onClick={() => void openAttachment('inline')}
                      disabled={opening}
                      data-testid="business-inquiry-attachment-open"
                    >
                      {detail.attachment.fileName}
                    </button>
                    <span style={{ color: 'var(--content-muted)' }}>{formatFileSize(detail.attachment.size)}</span>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => void openAttachment('attachment')}
                      disabled={opening}
                      data-testid="business-inquiry-attachment-download"
                    >
                      <Download size={14} aria-hidden="true" />
                      다운로드
                    </Button>
                  </span>
                ) : (
                  '첨부 없음'
                ),
              ],
            ]}
          />
        </SectionCard>
      ) : null}

      <SectionCard title="문의자 정보" padded={false}>
        <InfoRows
          rows={[
            ['회사명', detail.companyName || '—'],
            ['이름', detail.name],
            [
              '이메일',
              <a href={`mailto:${detail.email}`} className="underline underline-offset-2">
                {detail.email}
              </a>,
            ],
            ['연락처', detail.phone],
            ['개인정보 동의', detail.privacyAgreedAt ? `동의 · ${formatKst(detail.privacyAgreedAt, true)}` : '—'],
          ]}
        />
      </SectionCard>

      <SectionCard title="메일 발송 이력" padded={false}>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="business-inquiry-mail-logs">
            <thead>
              <tr style={{ background: 'var(--surface-1)' }}>
                {['수신 담당자', '이메일', '수신 사유', '발송일시', '결과', ''].map((h, i) => (
                  <th key={i} className="whitespace-nowrap px-4 py-2 text-center font-semibold">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {detail.mailLogs.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-4 py-4 text-center" style={{ color: 'var(--content-muted)' }}>
                    발송 이력이 없습니다.
                  </td>
                </tr>
              ) : (
                detail.mailLogs.map((log) => (
                  <tr key={log.logId} className="border-t" style={{ borderColor: 'var(--divider-default)' }} data-testid="business-inquiry-mail-log">
                    <td className="px-4 py-2 text-center">
                      {log.recipientKind === 'DEFAULT' ? '기본 수신 주소' : (log.recipientName ?? log.adminUserId ?? '—')}
                    </td>
                    <td className="px-4 py-2 text-center">{log.recipientEmail}</td>
                    <td className="px-4 py-2 text-center">{log.reasons.map((r) => r.label).join(', ')}</td>
                    <td className="whitespace-nowrap px-4 py-2 text-center tabular-nums">{formatKst(log.sentAt) ?? '—'}</td>
                    <td className="px-4 py-2 text-center" title={log.resultMessage ?? undefined}>
                      <Badge variant={log.status === 'SENT' ? 'success' : 'danger'} size="sm">
                        {log.status === 'SENT' ? '성공' : '실패'}
                      </Badge>
                      {log.attemptCount > 1 ? (
                        <span className="ml-1 text-[12px]" style={{ color: 'var(--content-muted)' }}>
                          ({log.attemptCount}회)
                        </span>
                      ) : null}
                    </td>
                    <td className="px-4 py-2 text-center">
                      {log.resendable && canWrite ? (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => onResend(log.logId)}
                          disabled={resend.isPending}
                          data-testid="business-inquiry-resend"
                        >
                          재발송
                        </Button>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </SectionCard>

      <SectionCard title="처리 관리" padded={false}>
        <InfoRows
          rows={[
            [
              '처리 상태',
              <RadioGroup
                orientation="horizontal"
                value={status}
                onValueChange={(v) => setStatus(v as InquiryStatus)}
                options={INQUIRY_STATUSES.map((s) => ({ value: s, label: INQUIRY_STATUS_LABELS[s] }))}
                disabled={!canWrite}
                aria-label="처리 상태"
                className="flex-wrap gap-x-4 gap-y-1"
              />,
            ],
            [
              '내부 메모',
              <div>
                <textarea
                  value={memo}
                  onChange={(e) => setMemo(e.target.value)}
                  maxLength={MEMO_MAX}
                  rows={4}
                  disabled={!canWrite}
                  placeholder="담당자 메모 (문의자에게 노출되지 않음)"
                  aria-label="내부 메모"
                  className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
                  style={{ borderColor: 'var(--content-muted)' }}
                  data-testid="business-inquiry-memo"
                />
                <p className="text-right text-[12px] tabular-nums" style={{ color: 'var(--content-muted)' }}>
                  {memo.length.toLocaleString()} / {MEMO_MAX.toLocaleString()}
                </p>
              </div>,
            ],
            [
              '최종 수정',
              updatedBy ? (
                <span data-testid="business-inquiry-updated">
                  {updatedBy} · <span className="tabular-nums">{formatKst(detail.updatedAt, true) ?? '—'}</span>
                </span>
              ) : (
                <span style={{ color: 'var(--content-muted)' }}>— (저장 시 수정자 · 일시 기록)</span>
              ),
            ],
            ['파기 예정일', detail.purgeScheduledOn ? `${detail.purgeScheduledOn} (수집일로부터 1년)` : '—'],
          ]}
        />
      </SectionCard>

      <div className="flex justify-center gap-2">
        <Button type="button" variant="outline" onClick={onBack} data-testid="business-inquiry-back">
          목록
        </Button>
        {canWrite ? (
          <Button type="button" variant="primary" onClick={onSave} disabled={update.isPending} data-testid="business-inquiry-save">
            저장
          </Button>
        ) : null}
      </div>
    </div>
  );
};
